import { Fragment, ReactNode, useMemo } from "react";
import { ZodSchema } from "zod";
import { PersistableView, View } from "@common/types/views/views";
import { keepTaskSelectionOptions } from "@components/drawers/taskCreationPreview";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { SubScopeProvider } from "@state/subScopeContext";
import { ResolvedTaskView } from "./useResolvedTaskView";

export interface TemplateTaskTableProps<TView extends View> {
    view: TView;
    onViewChange: (view: View) => void;
    emptyView: () => ReactNode;
}

interface Props<TView extends View> {
    /** The template's own view: its columns and options are what the table shows. */
    templateView: TView;
    onTemplateViewChange: (view: TView) => void;
    resolvedView: ResolvedTaskView;
    workspaceId: string | null;
    viewSchema: ZodSchema<TView>;
    /** Plural noun for the rows, used in the "no matches" empty state. */
    rowsLabel: string;
    renderTable: (props: TemplateTaskTableProps<TView>) => ReactNode;
}

const toPreviewView = <TView extends View>({
    templateView,
    apiFilters,
    viewSchema,
}: {
    templateView: TView;
    apiFilters: PersistableView["filters"];
    viewSchema: ZodSchema<TView>;
}): TView | undefined => {
    const parsed = viewSchema.safeParse({
        ...templateView,
        filters: apiFilters,
        options: {
            ...templateView.options,
            selection: false,
            bulkActions: false,
            readonly: true,
        },
    });
    return parsed.success ? parsed.data : undefined;
};

/**
 * The rows a template task would show for the previewed client. The resolved
 * filters come from the backend, so what is shown is what applying the template
 * creates. Read-only: no selection or bulk actions. Only the table runs in the
 * previewed client's scope; the header stays in the organization's.
 */
export const TemplateTaskDataTable = <TView extends View>({
    templateView,
    onTemplateViewChange,
    resolvedView,
    workspaceId,
    viewSchema,
    rowsLabel,
    renderTable,
}: Props<TView>) => {
    const { apiFilters, skipReason, isLoading } = resolvedView;
    const previewView = useMemo(
        () =>
            apiFilters
                ? toPreviewView({ templateView, apiFilters, viewSchema })
                : undefined,
        [templateView, apiFilters, viewSchema],
    );
    const filtersKey = JSON.stringify(apiFilters);
    const emptyView = useMemo(
        () =>
            apiFilters
                ? () => (
                      <EmptyState
                          header={`No ${rowsLabel} match this task`}
                          body="Update the filters, or preview another client or entity."
                          emphasizeHeader={false}
                      />
                  )
                : () => (
                      <EmptyState
                          header="Nothing to preview"
                          body={
                              skipReason ??
                              "Update the filters to preview this task."
                          }
                          emphasizeHeader={false}
                      />
                  ),
        [apiFilters, skipReason, rowsLabel],
    );

    if (isLoading) {
        return <Loader />;
    }
    if (!previewView) {
        return emptyView();
    }

    const handleViewChange = (next: View) =>
        onTemplateViewChange({
            ...templateView,
            columns: next.columns,
            options: keepTaskSelectionOptions({
                previewOptions: next.options,
                taskOptions: templateView.options,
            }),
        });

    return (
        <SubScopeProvider value={{ workspaceId }}>
            <Fragment key={filtersKey}>
                {renderTable({
                    view: previewView,
                    onViewChange: handleViewChange,
                    emptyView,
                })}
            </Fragment>
        </SubScopeProvider>
    );
};
